/** Field-level context help for the AI Import launch form (see FieldHelp). */
import type { FieldHelpEntry } from '@/components/FieldHelp';

export const AI_IMPORT_HELP = {
  mode: {
    title: 'What to create',
    body: 'A whole-catalog run crawls a site from your seed URLs and drafts every tour, itinerary, and activity it finds. The single-record modes treat each URL as one page to extract into that one kind of record.',
    story: 'ai-import',
  },
  urls: {
    title: 'Website URLs',
    body: 'One URL per line. In whole-catalog mode these are seed pages the crawler walks out from (same domain, robots.txt respected); in the single-record modes each URL is one page to extract. Optional if you upload documents instead.',
    story: 'ai-import',
  },
  documents: {
    title: 'Documents',
    body: 'Upload brochures instead of, or alongside, URLs. PDFs are read natively (layout and images); DOCX, RTF, and TXT as text. Useful when the catalog lives in a sales deck rather than on a website.',
    story: 'ai-import',
  },
  model: {
    title: 'Extraction model',
    body: 'The LLM that reads pages and drafts records. The figure shown is the cost per million input and output tokens; a stronger model extracts more accurately but spends the budget faster.',
    story: 'ai-import',
  },
  budget: {
    title: 'Budget cap (USD)',
    body: 'A hard ceiling on this run. Crawling stops at 80% of the cap and extraction at 100%, so hitting it yields a partial — but fully reviewable — draft set rather than a failure.',
    story: 'ai-import',
  },
  workers: {
    title: 'Parallel workers',
    body: 'How many pages to crawl and extract at once. Higher is faster but hits the target site harder; 1 is sequential and gentlest. Capped per run.',
    story: 'ai-import',
  },
  maxPages: {
    title: 'Max pages',
    body: 'The most pages the catalog crawler will fetch before it stops — a guardrail against wandering into a huge site. Blank uses the default cap.',
    story: 'ai-import',
  },
  maxDepth: {
    title: 'Max depth',
    body: 'How many links deep the crawler follows from the seed URLs. 0 = the seed pages only; blank = no depth limit (still bounded by max pages and budget).',
    story: 'ai-import',
  },
  sourceOfTruth: {
    title: 'Source of truth',
    body: 'When the tenant has a booking integration, importing the website next to that unacknowledged catalog would silently duplicate tours. Choose Booking system to match web drafts against real products, or Website only when there is nothing to reconcile against.',
    story: 'ai-import',
  },
  googlePlaces: {
    title: 'Google Places enrichment',
    body: 'After extraction, match each activity to a Google Place and fill in its address, coordinates, rating, opening hours, and photos. It only fills gaps (never overwrites extracted values), and you confirm every match before import.',
    story: 'ai-import',
  },
  dryRun: {
    title: 'Dry run',
    body: 'Crawl, extract, and stage drafts for review as normal, but skip the final import. A rehearsal to see what a site yields — and what it costs — without writing anything.',
    story: 'ai-import',
  },
} as const satisfies Record<string, FieldHelpEntry>;
